import React, { memo } from "react";
import classNames from "classnames";
import { useLabels } from "../../../hooks/useLabels";
import { labels } from "../../bottom-text-menus/bottom-text-menu/labels";
import { useAddTextZone } from "../../../hooks/useAddTextZone";
import { useAppContext } from "../../../hooks/useAppContext";
import { useKeyDown } from "../../../hooks/useKeyboard";
import { useTextZoneTextValue } from "../../bottom-text-menus/text-value-panel/use-text-zone-text-value";
import { TextAreaWithCount } from "../../text-area-with-count/text-area-with-count";

import "./sidebar-text-panel.css";
import { SidebarPanel } from "../sidebar-panel";
import { Icon } from "../../icon";
import { Button } from "../../button";

const STANDALONE_SHORTCUT_KEYS = new Set([
	"Delete",
	"ArrowLeft",
	"ArrowRight",
	"ArrowUp",
	"ArrowDown",
	"Escape",
	"Tab",
]);

function isShortcutKey(event: KeyboardEvent): boolean {
	if (event.ctrlKey || event.metaKey || event.altKey) return true;
	return STANDALONE_SHORTCUT_KEYS.has(event.key);
}

export const SidebarTextPanel = memo(function SidebarTextPanel() {
	const { canAddTextZoneToDefinition, canAddTextZoneToSpread, addTextZone } = useAddTextZone();
	const { googleAnalytics } = useAppContext();
	const [[addTextZoneText, selectTextZoneFirstText], areLabelsLoading] = useLabels([
		labels.addTextZone,
		labels.selectTextZoneFirst,
	]);
	const {
		textZone,
		textValue,
		placeholderText,
		canEdit,
		maxTextLength,
		inputRef,
		updateText,
		defaultPlaceholderText,
	} = useTextZoneTextValue();
	const showSelectTextZoneLabel = !textZone;

	// if keypress is not a shortcut, send input to textArea
	// this way the user can start typing immediately, and can still use our shortcuts
	// clicking in the textInput field will put focus there and enable shortcuts of the textArea (copy/paste text instead of zone)
	useKeyDown(
		(event) => {
			if (isShortcutKey(event)) return;

			if (event.key === "Backspace") {
				updateText(textValue.slice(0, -1));
				event.preventDefault();
			} else if (event.key === "Enter") {
				updateText(`${textValue}\n`);
				event.preventDefault();
			} else if (event.key.length === 1) {
				updateText(`${textValue}${event.key}`);
				event.preventDefault();
			}
		},
		{ enabled: !!textZone && canEdit },
	);

	if (areLabelsLoading) return null;

	return (
		<SidebarPanel.Root className="sidebar-text-panel">
			{canAddTextZoneToDefinition && (
				<SidebarPanel.Header>
					<Button
						className="sidebar-text-panel__add-textzone-button"
						disabled={!canAddTextZoneToSpread}
						onClick={() => {
							addTextZone();
							googleAnalytics.trackEventToGa({
								trackingAction: "TextZoneAddClicked",
							});
						}}
					>
						<Icon iconName="plus" />
						{addTextZoneText}
					</Button>
				</SidebarPanel.Header>
			)}
			<SidebarPanel.Content scrollable fade>
				{showSelectTextZoneLabel ? (
					<div className="sidebar-text-panel__select-textzone-label">
						{selectTextZoneFirstText}
					</div>
				) : (
					<TextAreaWithCount
						className={classNames("sidebar-text-panel__textarea", {
							"sidebar-text-panel__textarea--active": !!textZone && canEdit,
						})}
						rows={4}
						disabled={!textZone || !canEdit}
						placeholder={placeholderText || defaultPlaceholderText}
						value={textValue}
						ref={inputRef}
						aria-label="Text input"
						maxLength={maxTextLength}
						onInput={(event) => {
							event.stopPropagation();
							event.preventDefault();
							updateText(event.currentTarget.value);
						}}
					/>
				)}
			</SidebarPanel.Content>
		</SidebarPanel.Root>
	);
});
